"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import { stripLocalePrefix } from "@/i18n/routing";

/**
 * Thin gold progress bar at the top of the viewport.
 * Mimics NProgress: quick start, gradual slow-down, snap to 100% on arrival.
 */
export default function NavigationProgress() {
  const t = useTranslations("a11y");
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const prevUrl = useRef(pathname + searchParams.toString());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTicking = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startLoading = useCallback(() => {
    stopTicking();
    setProgress(0);
    setVisible(true);

    // Small delay so the browser paints at 0 before transitioning
    requestAnimationFrame(() => {
      setProgress(15);

      // Gradually increment — slows down as it approaches 90%
      tickRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          // Smaller increments as we get further along
          const step = prev < 30 ? 3 : prev < 50 ? 2 : prev < 70 ? 1 : 0.5;
          return Math.min(prev + step, 90);
        });
      }, 300);
    });
  }, [stopTicking]);

  const completeLoading = useCallback(() => {
    stopTicking();
    setProgress(100);
    // Fade out after reaching 100%
    const t = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 400);
    return t;
  }, [stopTicking]);

  // Navigation completed — snap to 100%
  useEffect(() => {
    const url = pathname + searchParams.toString();
    if (url === prevUrl.current) return;
    prevUrl.current = url;

    const timer = completeLoading();
    return () => clearTimeout(timer);
  }, [pathname, searchParams, completeLoading]);

  // Listen for clicks on internal links to START the bar
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("#") || href.startsWith("mailto:")) return;
      if (anchor.target === "_blank") return;
      // Compare locale-agnostically: on /vi, a hardcoded href="/" navigates to
      // the same page (middleware redirects / → /vi) so the pathname never
      // changes — without this the bar would start and never complete.
      if (stripLocalePrefix(href) === stripLocalePrefix(pathname)) return;
      startLoading();
    }
    document.addEventListener("click", handleClick, { capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, [pathname, startLoading]);

  // Cleanup on unmount
  useEffect(() => () => stopTicking(), [stopTicking]);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-nav h-[3px]"
      role="progressbar"
      aria-label={t("loadingPage")}
      aria-valuenow={Math.round(progress)}
    >
      <div
        className="h-full bg-gold"
        style={{
          width: `${progress}%`,
          opacity: progress === 100 ? 0 : 1,
          transitionProperty: progress === 0 ? "none" : progress === 100 ? "width, opacity" : "width",
          transitionDuration: progress === 100 ? "200ms, 400ms" : "300ms",
          transitionTimingFunction: progress === 100 ? "ease-out" : "linear",
        }}
      />
    </div>
  );
}
